import { Injectable } from "@angular/core";
import {
  Facebook,
  FacebookLoginResponse,
} from "@awesome-cordova-plugins/facebook/ngx";
import { Platform } from "@ionic/angular/standalone";
import { AppConfig } from "src/app/app.config";
import { LocalStorageKey } from "src/app/local-storage-key";
import { FacebookProfile } from "../../models/signup-data";
import { Util } from "../util/util";

type ForFacebookRedirectURL = {
  facebook_app_id: string;
  domain: string;
};

@Injectable({
  providedIn: "root",
})
export class FacebookProvider {
  __fbSdkReady = false;
  __fbCallbacks: any = [];
  FACEBOOK_APP_ID_FOR_PATO = "1177390178977826";
  FACEBOOK_APP_ID_FOR_MEMBER = "2425549100848504";

  constructor(
    private fbApp: Facebook,
    public platform: Platform
  ) {
    this.__fbSdkReady = true;

    for (const __fbCallback of this.__fbCallbacks) {
      __fbCallback();
    }
  }

  api(requestPath: string, permissions: string[]): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.api(requestPath, permissions);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.api(requestPath, permissions);
        });
      }

      // JS API does not take additional permissions
      FB.api(requestPath, (response) => {
        if (response.error) {
          reject(response);
        } else {
          resolve(response);
        }
      });
    });
  }

  getProfile(accessToken: string): Promise<FacebookProfile> {
    return new Promise((resolve, reject) => {
      const requestPath =
        "/me?fields=friends.limit(0),birthday,education,email,work,hometown,location,first_name,last_name,gender&access_token=" +
        accessToken;
      this.api(requestPath, null).then(
        (res) => {
          resolve(new FacebookProfile(res));
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  login(): Promise<FacebookLoginResponse> {
    let permissions = [];
    if (AppConfig.META_DATA.appID === "pato") {
      permissions = ["public_profile", "email"];
    } else {
      permissions = ["public_profile", "email"];
    }
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.login(permissions);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.login();
        });
      }
      // JS SDK takes an object here but the native SDKs use array.
      const options: any = {};
      if (permissions && permissions.length > 0) {
        const index = permissions.indexOf("rerequest");
        if (index > -1) {
          permissions.splice(index, 1);
          options.auth_type = "rerequest";
        }
        options.scope = permissions.join(",");
      }

      FB.login((response) => {
        if (response.authResponse) {
          resolve(response);
        } else if (response) {
          if (response.status) {
            reject(response.status.message);
          } else {
            reject(response);
          }
        } else {
          resolve(null);
        }
      }, options);
    });
  }

  logout(): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.logout();
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.logout();
        });
      }

      FB.logout((response) => {
        resolve(response);
      });
    });
  }

  getLoginStatus(): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.getLoginStatus();
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.getLoginStatus();
        });
      }

      FB.getLoginStatus((response) => {
        resolve(response);
      });
    });
  }

  getAccessToken(): Promise<string> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.getAccessToken();
    }
    return new Promise((resolve, reject) => {
      const response = FB.getAccessToken();
      if (response) {
        resolve(response);
        return;
      }
      reject("NO_TOKEN");
    });
  }

  showDialog(options: any): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.showDialog(options);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.showDialog(options);
        });
      }

      options.name = options.name || "";
      options.message = options.message || "";
      options.caption = options.caption || "";
      options.description = options.description || "";
      options.href = options.href || "";
      options.picture = options.picture || "";
      options.quote = options.quote || "";

      FB.ui(options, (response) => {
        if (response && (response.request || !response.error_code)) {
          resolve(response);
          return;
        }
        reject(response.message);
      });
    });
  }

  apiForFacebook(requestPath: string, permissions: string[]): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.api(requestPath, permissions);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.apiForFacebook(requestPath, permissions);
        });
      }

      // JS API does not take additional permissions
      FB.api(requestPath, (response) => {
        if (response.error) {
          reject(response);
        } else {
          resolve(response);
        }
      });
    });
  }

  logEvent(
    eventName: string,
    params?: object,
    valueToSum?: number
  ): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.logEvent(eventName, params, valueToSum);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.logEvent(eventName, params, valueToSum);
        });
      }

      FB.AppEvents.logEvent(eventName, valueToSum, params);

      if (resolve) {
        resolve(null);
      }
    });
  }

  logPurchase(value: number, currency: string): Promise<any> {
    if (!AppConfig.IS_BROWSER) {
      return this.fbApp.logPurchase(value, currency);
    }
    return new Promise((resolve, reject) => {
      if (!this.__fbSdkReady) {
        return this.__fbCallbacks.push(() => {
          this.logPurchase(value, currency);
        });
      }

      FB.AppEvents.logPurchase(value, currency);

      if (resolve) {
        resolve(null);
      }
    });
  }

  authFacebook(): void {
    const state = Util().random();
    localStorage.setItem(LocalStorageKey.User.State, state);
    const obj = this.decideVariableForFacebook();
    const facebook_app_id = obj["facebook_app_id"];
    const domain = obj["domain"];
    const redirectUrl = "&redirect_uri=" + domain;
    const facebookURL =
      "https://www.facebook.com/v10.0/dialog/oauth?client_id=" +
      facebook_app_id +
      "&redirect_uri=" +
      redirectUrl +
      "&state=" +
      state;
    document.location.href = facebookURL;
  }

  // 使用端末、MemberかPatoによって、facebook_app_id、redirectURLに使用するdomainを決める関数
  decideVariableForFacebook(): ForFacebookRedirectURL {
    let domain = "";
    let facebook_app_id = "";
    if (AppConfig.META_DATA.appID === "pato") {
      facebook_app_id = this.FACEBOOK_APP_ID_FOR_PATO;
      if (AppConfig.IS_BROWSER) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLBrowser;
      } else if (this.platform.is("android")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLAndroid;
      } else if (this.platform.is("ios")) {
        domain = AppConfig.META_DATA.oauthLoginRedirectURLiOS;
      }
    } else if (AppConfig.META_DATA.appID === "member") {
      facebook_app_id = this.FACEBOOK_APP_ID_FOR_MEMBER;
      domain = AppConfig.META_DATA.oauthLoginRedirectURLMember;
    }
    const obj: ForFacebookRedirectURL = {
      facebook_app_id: facebook_app_id,
      domain: domain,
    };
    return obj;
  }
}
